/**
 * useNearbyProducts — fetches a deduplicated pool of products across
 * nearby stores, with a single network call per store. Used by the
 * home page rails (Deals / Trending / Recommended).
 *
 * Strategy:
 *   1. Get nearby stores from `storesAPI.listStores(0, N)` — currently
 *      no lat/lng required (the seed dataset is small enough).
 *   2. For each store, pull `listProducts(storeId, 0, 30, true)` in parallel.
 *   3. Merge into a single array, attaching store name to each row for
 *      attribution in cards.
 *
 * Why fetch client-side? We don't have a "global product feed" endpoint yet
 * and adding one needs a backend change. This is fast enough for the home
 * page (≤ 5 stores × 30 products = 150 cards max).
 */
import { useEffect, useState } from 'react';
import { storesAPI, Store } from '../api/stores';
import { productsAPI, Product } from '../api/products';

export interface NearbyProduct extends Product {
  store_name?: string;
}

interface State {
  loading: boolean;
  products: NearbyProduct[];
  stores: Store[];
  error: string | null;
}

/** Module-level soft cache so navigating back to / doesn't refetch everything. */
interface CacheEntry { key: string; data: State; at: number; }
let _cache: CacheEntry | null = null;
const TTL_MS = 60_000; // 1 minute

export function useNearbyProducts(opts?: { storeLimit?: number; perStore?: number }) {
  const storeLimit = opts?.storeLimit ?? 5;
  const perStore = opts?.perStore ?? 30;
  const cacheKey = `${storeLimit}:${perStore}`;

  const [state, setState] = useState<State>(
    () =>
      _cache && _cache.key === cacheKey && Date.now() - _cache.at < TTL_MS
        ? _cache.data
        : { loading: true, products: [], stores: [], error: null },
  );

  useEffect(() => {
    let cancelled = false;
    if (_cache && _cache.key === cacheKey && Date.now() - _cache.at < TTL_MS) return;

    (async () => {
      try {
        setState((s) => ({ ...s, loading: true, error: null }));
        const stores = await storesAPI.listStores(0, storeLimit).catch(() => [] as Store[]);
        const productLists = await Promise.all(
          stores.map((s) =>
            productsAPI.listProducts(s.id, 0, perStore, true).then((ps) =>
              ps.map((p) => ({ ...p, store_name: s.business_name })),
            ).catch(() => [] as NearbyProduct[]),
          ),
        );
        const merged: NearbyProduct[] = ([] as NearbyProduct[]).concat(...productLists);
        if (cancelled) return;
        const data: State = { loading: false, products: merged, stores, error: null };
        _cache = { key: cacheKey, data, at: Date.now() };
        setState(data);
      } catch (err: any) {
        if (cancelled) return;
        setState({ loading: false, products: [], stores: [], error: err?.message || 'Failed to load' });
      }
    })();
    return () => { cancelled = true; };
  }, [storeLimit, perStore, cacheKey]);

  return state;
}

export default useNearbyProducts;

